import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';

interface PromoCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingId: string | null;
}

interface FormData {
  code: string;
  description: string;
  discountType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  discountValue: number;
  validFrom: string;
  validTo: string;
  maxUses: string;
  maxUsesPerCustomer: number;
  minBookingAmount: string;
  isActive: boolean;
}

const initialFormData: FormData = {
  code: '',
  description: '',
  discountType: 'PERCENTAGE',
  discountValue: 10,
  validFrom: new Date().toISOString().split('T')[0] ?? '',
  validTo: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ?? '',
  maxUses: '',
  maxUsesPerCustomer: 1,
  minBookingAmount: '',
  isActive: true,
};

export function PromoCodeModal({ isOpen, onClose, editingId }: PromoCodeModalProps) {
  const { t } = useTranslation('promoCodes');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);

  const { data: existingCode, isLoading: isLoadingCode } = trpc.promoCode.byId.useQuery(
    editingId!,
    { enabled: !!editingId }
  );

  const createMutation = trpc.promoCode.create.useMutation({
    onSuccess: () => {
      onClose();
      setFormData(initialFormData);
    },
    onError: (err) => setError(err.message),
  });

  const updateMutation = trpc.promoCode.update.useMutation({
    onSuccess: () => {
      onClose();
      setFormData(initialFormData);
    },
    onError: (err) => setError(err.message),
  });

  useEffect(() => {
    if (existingCode) {
      setFormData({
        code: existingCode.code,
        description: existingCode.description || '',
        discountType: existingCode.discountType as 'PERCENTAGE' | 'FIXED_AMOUNT',
        discountValue: existingCode.discountValue,
        validFrom: new Date(existingCode.validFrom).toISOString().split('T')[0] ?? '',
        validTo: new Date(existingCode.validTo).toISOString().split('T')[0] ?? '',
        maxUses: existingCode.maxUses?.toString() || '',
        maxUsesPerCustomer: existingCode.maxUsesPerCustomer,
        minBookingAmount: existingCode.minBookingAmount?.toString() || '',
        isActive: existingCode.isActive,
      });
    } else if (!editingId) {
      setFormData(initialFormData);
    }
  }, [existingCode, editingId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const payload = {
      code: formData.code,
      description: formData.description || undefined,
      discountType: formData.discountType,
      discountValue: formData.discountValue,
      validFrom: new Date(formData.validFrom),
      validTo: new Date(formData.validTo),
      maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
      maxUsesPerCustomer: formData.maxUsesPerCustomer,
      minBookingAmount: formData.minBookingAmount ? parseFloat(formData.minBookingAmount) : undefined,
      isActive: formData.isActive,
      applicablePackageIds: [],
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold">
            {editingId ? t('modal.editTitle') : t('modal.createTitle')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        {isLoadingCode ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>
            )}

            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('modal.code')} *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder={t('modal.codePlaceholder')}
                className="mt-1 w-full rounded-lg border p-2.5 font-mono uppercase focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                required
                minLength={3}
                maxLength={30}
                disabled={!!editingId}
              />
              <p className="mt-1 text-xs text-gray-500">{t('modal.codeHint')}</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('modal.description')}</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('modal.descriptionPlaceholder')}
                className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Discount Type & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('modal.discountType')} *</label>
                <select
                  value={formData.discountType}
                  onChange={(e) =>
                    setFormData({ ...formData, discountType: e.target.value as 'PERCENTAGE' | 'FIXED_AMOUNT' })
                  }
                  className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="PERCENTAGE">{t('modal.percentage')}</option>
                  <option value="FIXED_AMOUNT">{t('modal.fixedAmount')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('modal.discountValue')} *</label>
                <div className="relative mt-1">
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) =>
                      setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full rounded-lg border p-2.5 pr-8 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    required
                    min={0}
                    max={formData.discountType === 'PERCENTAGE' ? 100 : undefined}
                    step={formData.discountType === 'PERCENTAGE' ? 1 : 0.01}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {formData.discountType === 'PERCENTAGE' ? '%' : '₺'}
                  </span>
                </div>
              </div>
            </div>

            {/* Valid Period */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('modal.validFrom')} *</label>
                <input
                  type="date"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                  className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('modal.validTo')} *</label>
                <input
                  type="date"
                  value={formData.validTo}
                  onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                  className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  required
                  min={formData.validFrom}
                />
              </div>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('modal.maxUses')}</label>
                <input
                  type="number"
                  value={formData.maxUses}
                  onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                  placeholder={t('modal.maxUsesHint')}
                  className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  min={1}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('modal.maxUsesPerCustomer')}</label>
                <input
                  type="number"
                  value={formData.maxUsesPerCustomer}
                  onChange={(e) =>
                    setFormData({ ...formData, maxUsesPerCustomer: parseInt(e.target.value) || 1 })
                  }
                  className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  min={1}
                  required
                />
              </div>
            </div>

            {/* Minimum Booking Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('modal.minBookingAmount')}</label>
              <input
                type="number"
                value={formData.minBookingAmount}
                onChange={(e) => setFormData({ ...formData, minBookingAmount: e.target.value })}
                placeholder={t('modal.minBookingHint')}
                className="mt-1 w-full rounded-lg border p-2.5 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                min={0}
                step={0.01}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                {t('modal.isActive')}
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('modal.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingId ? t('modal.save') : t('modal.create')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
